import { useEffect } from "react";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SystemNotification } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { GlassPanel } from "../GlassPanel/GlassPanel";
import { NotificationsBadge } from "./NotificationsBadge";
import { NotificationsPanelBody } from "./NotificationsPanelBody";
import { useMenuPopover } from "./useMenuPopover";

export interface NotificationsMenuProps {
  /**
   * Cuántas novedades sin leer. Llega POR PROPS a propósito: este
   * componente no pide nada al API ni sabe que existe uno — la barra
   * consulta y le pasa el resultado.
   */
  count: number;
  /** Las notificaciones, las más nuevas primero. */
  items?: SystemNotification[];
  /** Se dispara al ABRIR el panel: la barra marca todo como leído. */
  onOpen?: () => void;
  /** Sale de la campana pero NO se borra. */
  onArchive?: (id: string) => void;
  /** Se va de verdad. */
  onRemove?: (id: string) => void;
  className?: string;
}

/**
 * La campana de la barra superior.
 *
 * ── De dónde sale lo que enseña ──────────────────────────────────────
 * Todo entra por props (`count`, `items`) y la lectura sale por
 * `onOpen`: la BARRA es quien habla con `/api/notifications/me`. Esta
 * pieza no sabe que existe un API — el día que nació esperó así a su
 * backend, y cuando llegó solo cambió quién le pasa los números.
 *
 * ── Con cero no pinta un globo vacío ─────────────────────────────────
 * `NotificationsBadge` devuelve `null` con 0. Un contador siempre
 * encendido es ruido, y el ruido se deja de mirar.
 *
 * ── Y responde ───────────────────────────────────────────────────────
 * Pulsarla ABRE un panel que dice que no hay novedades. Es la diferencia
 * entre un botón terminado y un adorno: un botón que no hace nada al
 * pulsarlo se lee como una avería, y quien lo prueba lo intenta tres
 * veces antes de rendirse.
 *
 * ── Es una divulgación, no un menú ───────────────────────────────────
 * Sin `aria-haspopup="menu"` ni `role="menu"`, al revés que `UserMenu`:
 * ahí dentro no hay acciones que elegir, hay un texto que leer. Un
 * `role="menu"` con contenido suelto es contenido que muchos lectores se
 * saltan —solo anuncian los `menuitem`— y el mensaje se perdería entero.
 * `aria-expanded` + `aria-controls` es el patrón que corresponde: un
 * botón que muestra y esconde una región.
 *
 * El foco entra en el panel al abrir (de ahí el `tabIndex={-1}`), Escape
 * cierra y lo devuelve al botón, y un clic fuera cierra sin robarlo:
 * todo eso es `useMenuPopover`, el mismo que usa `UserMenu`.
 */
export function NotificationsMenu({
  count,
  items = [],
  onOpen,
  onArchive,
  onRemove,
  className,
}: NotificationsMenuProps) {
  const { t } = useTranslation();
  const { open, toggle, triggerRef, panelRef, panelId } = useMenuPopover();

  /*
   * Abrir la campana LEE: el globo se apaga (vía `onOpen`, que marca en
   * el servidor), la lista se queda con sus puntos hasta la próxima.
   */
  useEffect(() => {
    if (open) onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /*
   * El número va en el NOMBRE del botón y no solo en el globo: quien usa
   * un lector de pantalla se entera de que hay tres novedades sin tener
   * que abrir el panel, igual que quien las ve de un vistazo.
   */
  const label =
    count > 0
      ? t("Notificaciones, {{count}} sin leer", { count })
      : t("Notificaciones");

  return (
    <div className={cn("relative shrink-0", className)}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        iconOnly
        onClick={toggle}
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        leftIcon={<Bell />}
        className="h-9 w-9 border-ds-border bg-white/70"
      />

      <NotificationsBadge count={count} />

      {open && (
        <GlassPanel
          ref={panelRef}
          id={panelId}
          tabIndex={-1}
          /*
            ── Más ancho (Anahí, 29-ago) ──────────────────────────────
            Estaba en 17rem (272 px). Con títulos como "Tu documentación
            fue aprobada" cada aviso se partía en cinco renglones, y al
            abrir las acciones —que desplazan el texto 40 px— la frase se
            salía por la izquierda. 22rem le da sitio a las dos cosas.
          */
          className="absolute right-0 top-full z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-white/85 p-3 shadow-ds-float outline-none"
        >
          <p className="relative text-[13px] font-semibold leading-tight text-ds-ink">
            {t("Notificaciones")}
          </p>
          <NotificationsPanelBody items={items} onArchive={onArchive} onRemove={onRemove} />
        </GlassPanel>
      )}
    </div>
  );
}
